// Design System Chip Component
// Tags, labels, and removable items

import React from 'react';
import { Chip as MuiChip, styled, alpha } from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens, borderTokens } from '../../tokens';

// Styled Chip with design system tokens
const StyledChip = styled(MuiChip)(({ theme, color = 'default', clickable }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    borderRadius: borderTokens.full,
    fontWeight: 500,
    fontSize: '0.875rem',
    
    ...(clickable && {
      cursor: 'pointer',
      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: `0 2px 4px ${alpha(colors.text.primary, 0.1)}`,
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    }),
    
    '& .MuiChip-deleteIcon': {
      fontSize: '1.125rem',
      marginLeft: spacingTokens.component.xs,
      opacity: 0.7,
      transition: 'opacity 0.2s',
      
      '&:hover': {
        opacity: 1,
      },
    },
  };
});

/**
 * Chip - Tag/label component
 * 
 * Features:
 * - Clickable chips
 * - Deletable chips
 * - Icon support
 * - Multiple colors and variants
 * - Size options
 * 
 * @example
 * // Basic chip
 * <Chip label="React" />
 * 
 * // Deletable chip
 * <Chip label="JavaScript" onDelete={handleDelete} />
 * 
 * // Clickable chip with icon
 * <Chip 
 *   icon={<FaceIcon />} 
 *   label="Profile" 
 *   onClick={handleClick}
 *   color="primary"
 * />
 * 
 * // Outlined variant
 * <Chip label="Draft" variant="outlined" />
 */
const Chip = ({
  label,
  color = 'default',
  variant = 'filled',
  size = 'medium',
  icon,
  avatar,
  onDelete,
  onClick,
  clickable,
  disabled = false,
  ...props
}) => {
  const isClickable = clickable || !!onClick;
  
  return (
    <StyledChip
      label={label}
      color={color}
      variant={variant}
      size={size}
      icon={icon}
      avatar={avatar}
      onDelete={onDelete}
      onClick={onClick}
      clickable={isClickable}
      disabled={disabled}
      {...props}
    />
  );
};

Chip.propTypes = {
  label: PropTypes.node.isRequired,
  color: PropTypes.oneOf(['default', 'primary', 'secondary', 'error', 'warning', 'info', 'success']),
  variant: PropTypes.oneOf(['filled', 'outlined']),
  size: PropTypes.oneOf(['small', 'medium']),
  icon: PropTypes.element,
  avatar: PropTypes.element,
  onDelete: PropTypes.func,
  onClick: PropTypes.func,
  clickable: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default Chip;